<script setup lang="ts">
import { computed } from 'vue';
import { Head, Link } from '@inertiajs/vue3';
import { CheckCircleIcon } from '@heroicons/vue/24/solid';
import SiteHeader from '@/components/SiteHeader.vue';
import SiteFooter from '@/components/SiteFooter.vue';
import HomeLayout from '@/layouts/HomeLayout.vue';
import AddressQuoteForm from '@/components/AddressQuoteForm.vue';

interface Location {
    name: string;
    slug: string;
    state: string | null;
    meta_title: string | null;
    meta_description: string | null;
}

interface LocationSummary {
    name: string;
    state: string | null;
}

const props = defineProps<{
    location?: Location;
    locations?: LocationSummary[];
    jsonLdSchema?: string;
}>();

defineOptions({ layout: HomeLayout });

const heroTitle = computed(() =>
    props.location ? `${props.location.name} Lawn Mowing` : 'Lawn Mowing',
);
const heroTitleAccent = computed(() => 'Made Easy.');
const heroSubtext = computed(() =>
    props.location
        ? `Affordable & reliable lawn care in ${props.location.name}, ${props.location.state ?? 'TX'}.`
        : 'Affordable & reliable lawn care for homes & small businesses.',
);
const makesItEasyHeading = computed(() =>
    props.location
        ? `Affordable, Reliable,\nLawn Care in ${props.location.name}`
        : 'Affordable, Reliable,\nLocal Lawn Care',
);
const whatsIncludedHeading = 'Complete Lawn\nCare Services';
const localProsSubheading = computed(() =>
    props.location
        ? `The Best Lawn Mowing Service in ${props.location.name}`
        : 'The Best Local Lawn Mowing Service',
);

const pageTitle = computed(() =>
    props.location?.meta_title
        ? props.location.meta_title
        : 'Lawn Mowing Made Easy',
);
const pageDescription = computed(() =>
    props.location?.meta_description
        ? props.location.meta_description
        : 'Mow Managers makes lawn mowing easy with affordable, reliable service from local pros. Get a free instant price quote. Mowing, trimming, edging, and debris cleanup.',
);
const canonicalUrl = computed(() =>
    props.location
        ? `https://mowmanagers.com/${props.location.slug}`
        : 'https://mowmanagers.com/',
);

const steps = [
    {
        number: '01',
        title: 'Get a Free\nPrice Quote',
        description: 'Just enter your address and zip code',
    },
    {
        number: '02',
        title: 'Sign Up\nWith Ease',
        description: 'We make the process easy and hassle free',
    },
    {
        number: '03',
        title: 'Receive Your\nMow Schedule',
        description: 'Choose the mow day that works for you',
    },
    {
        number: '04',
        title: 'Trusted Pros\nMow Your Lawn',
        description: 'Enjoy dependable service and support',
    },
];

const services = ['Mow', 'Line Trim', 'Edge', 'Blow Debris'];
</script>

<template>
    <div class="bg-white">
        <Head :title="pageTitle">
            <meta
                head-key="description"
                name="description"
                :content="pageDescription"
            />
            <link rel="canonical" :href="canonicalUrl" />
            <meta
                property="og:title"
                :content="pageTitle.includes('Mow Managers') ? pageTitle : `${pageTitle} - Mow Managers`"
            />
            <meta property="og:description" :content="pageDescription" />
            <meta property="og:type" content="website" />
            <meta property="og:url" :content="canonicalUrl" />
            <meta
                property="og:image"
                content="https://mowmanagers.com/img/hero.png"
            />
            <meta
                property="og:image:alt"
                content="Mow Managers lawn care service"
            />
        </Head>

        <!-- ==================== HERO + NAV ==================== -->
        <div class="relative">
            <SiteHeader transparent />

            <!-- HERO -->
            <section
                class="relative flex min-h-[75vh] items-center bg-cover bg-center bg-no-repeat lg:min-h-[80vh]"
                :style="{ backgroundImage: `url('/img/hero.png')` }"
            >
                <div class="absolute inset-0 bg-black/50" />

                <div
                    class="relative z-10 mx-auto w-full max-w-7xl px-4 py-32 sm:px-6 lg:px-8 lg:py-40"
                >
                    <!-- city-template: heroTitle -->
                    <h1
                        class="text-5xl font-bold sm:text-6xl lg:text-[90px] lg:leading-[100px] xl:text-[110px] xl:leading-[114px]"
                    >
                        <span class="text-white">{{ heroTitle }}</span>
                        {{ ' ' }}<br />
                        <span class="text-mm-green">{{ heroTitleAccent }}</span>
                    </h1>

                    <!-- city-template: heroSubtext -->
                    <p class="mt-4 max-w-xl text-[18px] font-medium text-white">
                        {{ heroSubtext }}
                    </p>

                    <AddressQuoteForm variant="hero" input-id-prefix="hero" />
                </div>
            </section>
        </div>

        <!-- ==================== BELOW-HERO IMAGES (full width, overlapping hero) ==================== -->
        <div
            class="relative z-20 -mt-10 hidden gap-4 px-2 md:grid md:grid-cols-4 lg:-mt-16 lg:gap-6 lg:px-4"
        >
            <div class="overflow-hidden rounded-xl md:col-span-1">
                <img
                    src="/img/below-header-left.png"
                    alt="Lawn mower on grass"
                    class="aspect-[4/5] h-full w-full object-cover md:aspect-auto md:h-[340px] lg:h-[420px]"
                />
            </div>
            <div class="overflow-hidden rounded-xl md:col-span-2">
                <img
                    src="/img/below-header-middle.webp"
                    alt="Family enjoying lawn"
                    class="aspect-[4/5] h-full w-full object-cover md:aspect-auto md:h-[340px] lg:h-[420px]"
                />
            </div>
            <div class="overflow-hidden rounded-xl md:col-span-1">
                <img
                    src="/img/below-header-right.webp"
                    alt="Professional mowing"
                    class="aspect-[4/5] h-full w-full object-cover md:aspect-auto md:h-[340px] lg:h-[420px]"
                />
            </div>
        </div>

        <!-- ==================== MOW MANAGERS MAKES IT EASY ==================== -->
        <section
            aria-labelledby="easy-heading"
            class="bg-white pt-20 pb-12 lg:pt-28 lg:pb-16"
        >
            <div class="mx-auto max-w-7xl px-4 text-center sm:px-6 lg:px-8">
                <p
                    class="text-[21px] font-extrabold tracking-[4.2px] text-mm-green uppercase"
                >
                    Mow Managers Makes It Easy
                </p>
                <!-- city-template: makesItEasyHeading -->
                <h2
                    id="easy-heading"
                    class="mt-3 text-3xl font-bold whitespace-pre-line text-mm-dark sm:text-4xl lg:text-[90px] lg:leading-[94px]"
                >
                    {{ makesItEasyHeading }}
                </h2>
                <p
                    class="mx-auto mt-6 max-w-[628px] text-[20px] leading-[34px] font-medium text-[#6d706d]"
                >
                    We make lawn mowing easy with a modern, hassle-free process.
                    Enter your address, get a price instantly, and enjoy
                    dependable service on your schedule, with 24/7 text support.
                </p>
            </div>
        </section>

        <!-- ==================== 4 STEPS + DARK SECTION (overlapping) ==================== -->
        <!-- The green step cards sit on white, overlapping down into the dark green section below -->
        <div class="relative">
            <!-- Dark green background starts partway down so cards overlap into it -->
            <div class="absolute right-0 bottom-0 left-0 h-[55%] bg-mm-dark" />

            <div class="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <div
                    class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 lg:gap-6"
                >
                    <div
                        v-for="step in steps"
                        :key="step.number"
                        class="flex flex-col items-center rounded-[20px] bg-mm-green p-6 text-center lg:p-8"
                    >
                        <div
                            class="flex h-[74px] w-[74px] items-center justify-center rounded-full border-[3px] border-white/40"
                        >
                            <span
                                class="text-[36px] font-bold text-[#002900]"
                                >{{ step.number }}</span
                            >
                        </div>
                        <h3
                            class="mt-6 text-[26px] leading-none font-bold whitespace-pre-line text-white lg:text-[28px]"
                        >
                            {{ step.title }}
                        </h3>
                        <p
                            class="mt-4 max-w-[244px] text-[16px] font-medium text-[#002900] lg:text-[18px]"
                        >
                            {{ step.description }}
                        </p>
                    </div>
                </div>
            </div>
        </div>

        <!-- ==================== FREE PRICE QUOTE + WHAT'S INCLUDED (dark section) ==================== -->
        <section
            aria-labelledby="included-heading"
            class="bg-mm-dark pt-16 pb-20 lg:pt-20 lg:pb-28"
        >
            <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <!-- FREE PRICE QUOTE button -->
                <div class="mb-16 text-center lg:mb-20">
                    <Link
                        href="/get-started"
                        class="inline-block cursor-pointer rounded-md border-2 border-white px-10 py-3 text-sm font-bold tracking-[1.6px] text-white uppercase transition-colors hover:bg-white hover:text-mm-dark"
                    >
                        Free Price Quote
                    </Link>
                </div>

                <div
                    class="grid grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-16"
                >
                    <div>
                        <p
                            class="text-[21px] font-extrabold tracking-[4.2px] text-mm-green uppercase"
                        >
                            What's Included
                        </p>
                        <!-- city-template: whatsIncludedHeading -->
                        <h2
                            id="included-heading"
                            class="mt-3 text-3xl font-bold whitespace-pre-line text-white sm:text-4xl lg:text-[70px] lg:leading-[80px]"
                        >
                            {{ whatsIncludedHeading }}
                        </h2>
                        <p
                            class="mt-6 max-w-[416px] text-[20px] leading-[34px] font-medium text-white"
                        >
                            With Mow Managers, you get complete lawn care,
                            mowing, line trimming, edging, and debris cleanup,
                            all for one simple price.
                        </p>
                        <div class="mt-6 flex items-start gap-3">
                            <div
                                class="w-[4px] shrink-0 self-stretch rounded-full bg-mm-green"
                            />
                            <p
                                class="text-[20px] leading-[34px] font-medium text-white"
                            >
                                Ask about our Bush &amp; Bed,<br />and Grass
                                Bagging services.
                            </p>
                        </div>
                    </div>

                    <ul class="list-none space-y-4">
                        <li
                            v-for="service in services"
                            :key="service"
                            class="flex items-center gap-4"
                        >
                            <CheckCircleIcon
                                class="size-10 shrink-0 text-mm-green lg:size-12"
                                aria-hidden="true"
                            />
                            <span
                                class="text-[40px] leading-[94px] font-medium text-white lg:text-[52px]"
                                >{{ service }}</span
                            >
                        </li>
                    </ul>
                </div>
            </div>
        </section>

        <!-- ==================== LOCAL LAWN CARE PROS ==================== -->
        <!-- Pulled up slightly so the top of the image peeks into the dark section above -->
        <section
            aria-labelledby="pros-heading"
            class="relative z-10 -mt-12 bg-transparent pb-20 lg:-mt-16 lg:pb-28"
        >
            <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <div
                    class="grid grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-16"
                >
                    <div class="overflow-hidden rounded-[20px]">
                        <img
                            src="/img/local-lawn-care.webp"
                            alt="Local lawn care professional"
                            class="h-full w-full object-cover"
                        />
                    </div>

                    <div class="pt-12 lg:pt-16">
                        <!-- city-template: localProsSubheading -->
                        <p
                            class="text-[21px] font-extrabold tracking-[4.2px] text-mm-green uppercase"
                        >
                            {{ localProsSubheading }}
                        </p>
                        <h2
                            id="pros-heading"
                            class="mt-3 text-3xl font-bold text-mm-dark sm:text-4xl lg:text-[70px] lg:leading-[88px]"
                        >
                            Local Lawn Care<br />Pros You Can Trust
                        </h2>
                        <p
                            class="mt-6 max-w-[628px] text-[20px] leading-[34px] font-medium text-[#6d706d]"
                        >
                            All lawns aren't created equal, that's why we use
                            local, independent, experienced lawn care
                            professionals who we trust to take great care of
                            your lawn. We're committed to providing you with the
                            best customer service, fast and reliable
                            communication, and a freshly cut lawn that you can
                            be proud of.
                        </p>
                        <div
                            class="mt-8 flex flex-wrap justify-center gap-4 lg:justify-start"
                        >
                            <Link
                                href="/get-started"
                                class="cursor-pointer rounded-md bg-mm-green px-8 py-3 text-[16px] font-bold tracking-[1.6px] text-white uppercase transition-colors hover:bg-mm-green-dark"
                            >
                                Get Started
                            </Link>
                            <Link
                                href="/questions"
                                class="cursor-pointer rounded-md border-2 border-mm-green px-8 py-3 text-[16px] font-bold tracking-[1.6px] text-mm-green uppercase transition-colors hover:bg-mm-green hover:text-white"
                            >
                                Questions
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- ==================== RELIABLE EXPERTS ==================== -->
        <section
            aria-labelledby="experts-heading"
            class="bg-white py-20 lg:py-28"
        >
            <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <div class="mb-12 text-center lg:mb-16">
                    <p
                        class="text-[21px] font-extrabold tracking-[4.2px] text-mm-green uppercase"
                    >
                        Why People Trust Mow Managers
                    </p>
                    <h2
                        id="experts-heading"
                        class="mt-3 text-3xl font-bold text-mm-dark sm:text-4xl lg:text-[70px] lg:leading-[88px]"
                    >
                        Reliable Lawn Experts<br />You Can Count On
                    </h2>
                </div>

                <div
                    class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 lg:gap-6"
                >
                    <div
                        class="flex aspect-square flex-col items-center justify-center rounded-[20px] bg-mm-dark p-6 text-center"
                    >
                        <p
                            class="text-[14px] leading-[1.8] font-extrabold tracking-[2px] text-mm-green uppercase lg:text-[20px]"
                        >
                            Lawn Mowing<br />Done Right
                        </p>
                        <p
                            class="mt-2 text-[14px] leading-[1.8] font-semibold tracking-[0.8px] text-white lg:text-[18px]"
                        >
                            with Experienced<br />Lawn Care Pros
                        </p>
                    </div>

                    <div class="overflow-hidden rounded-[20px]">
                        <img
                            src="/img/bottom-square-1.png"
                            alt="Family on lawn"
                            class="aspect-square h-full w-full object-cover"
                        />
                    </div>

                    <div
                        class="flex aspect-square flex-col items-center justify-center rounded-[20px] bg-mm-dark p-6 text-center"
                    >
                        <p
                            class="text-[14px] leading-[1.8] font-extrabold tracking-[2px] text-mm-green uppercase lg:text-[20px]"
                        >
                            Fast Reliable<br />Customer Service
                        </p>
                        <p
                            class="mt-2 text-[14px] leading-[1.8] font-semibold tracking-[0.8px] text-white lg:text-[18px]"
                        >
                            with AI-powered<br />24/7 Text Support
                        </p>
                    </div>

                    <div class="overflow-hidden rounded-[20px]">
                        <img
                            src="/img/bottom-square-2.webp"
                            alt="Beautiful home with lawn"
                            class="aspect-square h-full w-full object-cover"
                        />
                    </div>
                </div>
            </div>
        </section>

        <!-- ==================== FOOTER ==================== -->
        <SiteFooter />
    </div>
</template>
